import React from 'react';
import { useSelector } from 'react-redux';

import { getUserType } from 'redux/slices/userSlice';

import { StudentGapLetter, StudentGapWord } from './ui';
import { ICL, TaskKeysType, GapTask, GapWord, GapTaskTypes, Exam, WorkType, ExamStatus } from '../../../shared/types';
import StudentTaskWrapper from '../StudentTaskWrapper';

interface StudentGapTaskProps extends GapTask {
  icl: ICL | Exam | null;
  handleChangeTaskByKey: (id: string, key: TaskKeysType, value: string[] | string | boolean | GapWord[]) => void;
  saveTaskAnswer: (id: string) => Promise<void>;
  hw?: boolean;
  preview?: boolean;
  studentName?: string;
  index: number;
  points: number;
  workTaskType?: WorkType;
}

const gapComponentByType = {
  [GapTaskTypes.letters]: StudentGapLetter,
  [GapTaskTypes.words]: StudentGapWord,
};

export default function StudentGapTask({
  icl,
  text,
  subText,
  answer,
  hasAnswer,
  hasStar,
  checkedAt,
  completedAt,
  handleChangeTaskByKey,
  id,
  hasPhoto,
  answersPhotos,
  saveTaskAnswer,
  deadline,
  audioUrl,
  videoUrl,
  taskPhotos,
  editedAnswerPhotos,
  hw,
  pdfUrl,
  wordUrl,
  studentPdfUrl,
  studentWordUrl,
  preview,
  gapWords,
  gapTaskType,
  maxGrade,
  studentName,
  index,
  points,
  workTaskType,
  teacherPoints,
}: StudentGapTaskProps) {
  const userType = useSelector(getUserType);

  const isExam = icl?.type === WorkType.exam;
  const isExamCompleted = isExam && icl?.status === ExamStatus.readyToCheck;
  const areStudentAnswersCorrect = gapWords.every(gapWord => gapWord.answer === gapWord.correctAnswer);

  const changeFormikGapWords = (currentGapWord: GapWord) => {
    const changedGapWords = gapWords.map(gapWord => (gapWord.id === currentGapWord.id ? currentGapWord : gapWord));

    handleChangeTaskByKey(id, 'gapWords', changedGapWords);
  };

  const areStudentAnswers = gapWords.every(gapWord => (gapWord.word.includes('_') ? !!gapWord.answer : true));
  const isDisabledSubmitButton = !areStudentAnswers || !!checkedAt || !!completedAt;

  const isStudent = userType === 'Student';
  const isInputDisabled = !!checkedAt || !!completedAt || !isStudent || !!preview;

  const GapComponent = gapComponentByType[gapTaskType];

  return (
    <StudentTaskWrapper
      icl={icl}
      taskText={text}
      answersPhotos={answersPhotos}
      id={id}
      handleChangeTaskByKey={handleChangeTaskByKey}
      checkedAt={checkedAt}
      hasPhoto={hasPhoto}
      completedAt={completedAt}
      saveTaskAnswer={saveTaskAnswer}
      deadline={deadline}
      audioUrl={audioUrl}
      answer={answer}
      hasAnswer={hasAnswer}
      hasStar={hasStar}
      videoUrl={videoUrl}
      taskPhotos={taskPhotos}
      editedAnswerPhotos={editedAnswerPhotos}
      hw={hw}
      pdfUrl={pdfUrl}
      wordUrl={wordUrl}
      studentPdfUrl={studentPdfUrl}
      studentWordUrl={studentWordUrl}
      preview={preview}
      subText={subText}
      isDisabledSubmitButton={isDisabledSubmitButton}
      isSubmitButton
      areStudentAnswersCorrect={areStudentAnswersCorrect}
      isExamCompleted={isExamCompleted}
      studentName={studentName}
      grade={isExamCompleted && areStudentAnswersCorrect ? maxGrade : 0}
      maxGrade={maxGrade}
      index={index}
      points={points}
      workTaskType={workTaskType}
      teacherPoints={teacherPoints}
    >
      <ul className="flex flex-wrap gap-x-2 gap-y-1 !py-5 lg:!py-8">
        {gapWords.map(gapWord => (
          <li key={gapWord.id}>
            {gapWord.word.includes('_') ? (
              <GapComponent
                gapWord={gapWord}
                isInputDisabled={isInputDisabled}
                changeFormikGapWords={changeFormikGapWords}
              />
            ) : (
              <p className="!mb-0 text-lg font-medium leading-[25px] lg:text-xl">{gapWord.word}</p>
            )}
          </li>
        ))}
      </ul>
    </StudentTaskWrapper>
  );
}
